import React, {useEffect, useMemo, useState} from 'react';
import {ArrowRight, X} from 'lucide-react';
import {useLocation} from 'react-router-dom';

import {emitProductAnalytics, emitProductAnalyticsOnce} from '../../utils/productAnalytics';
import {useConversion} from './ConversionContext';

function stateFor(remaining, limit) {
  if (remaining === 0) return 'exhausted';
  if (remaining === 1) return 'one_remaining';
  if (remaining === limit) return 'unused';
  return 'available';
}

export default function FreeStatusStrip({companyId, userId}) {
  const location = useLocation();
  const conversion = useConversion();
  const limit = Number(conversion.billing?.commercial?.limits?.freeLifetimeCreationLimit);
  const usageState = stateFor(conversion.creationRemaining, limit);
  const dismissalKey = useMemo(
    () => `affinityai:free-status-dismissed:v1:${userId}:${companyId}:${usageState}`,
    [companyId, usageState, userId],
  );
  const [dismissed, setDismissed] = useState(() => {
    try {
      return globalThis.localStorage?.getItem(dismissalKey) === 'true';
    } catch {
      return false;
    }
  });
  const search = new URLSearchParams(location.search || '');
  const contextualSurface =
    location.pathname.includes('/scenario/new') ||
    location.pathname.includes('/continue') ||
    location.pathname.endsWith('/invite') ||
    search.get('settings') === 'plan' ||
    Boolean(search.get('upgrade')) ||
    (location.pathname.endsWith('/dashboard') && conversion.aiFollowupRemaining === 0);

  useEffect(() => {
    try {
      setDismissed(globalThis.localStorage?.getItem(dismissalKey) === 'true');
    } catch {
      setDismissed(false);
    }
  }, [dismissalKey]);

  useEffect(() => {
    if (!conversion.isFree || dismissed || contextualSurface) return;
    emitProductAnalyticsOnce('upgrade_prompt_impression', `free-strip:${companyId}:${usageState}`, {
      reason: 'free_status',
      surface: 'free_status_strip',
      usageState,
      billingAuthority: conversion.canManageBilling,
      remainingCategory: usageState,
      returnAction: 'dashboard',
    });
  }, [companyId, contextualSurface, conversion.canManageBilling, conversion.isFree, dismissed, usageState]);
  if (!conversion.isFree || dismissed || contextualSurface || !Number.isFinite(limit)) return null;

  const primary =
    usageState === 'unused'
      ? `Free workspace · ${limit} Scenario maps included`
      : usageState === 'one_remaining'
        ? 'One free Scenario map remains'
        : usageState === 'exhausted'
          ? 'Free plan · Included Scenario maps used'
          : `Free workspace · ${conversion.creationRemaining} Scenario maps remain`;
  const supporting =
    usageState === 'unused'
      ? 'Create your first map and bring a decision into focus.'
      : 'Company keeps decisions moving with more active maps and unlimited follow-ups on active maps.';

  const dismiss = () => {
    try {
      globalThis.localStorage?.setItem(dismissalKey, 'true');
    } catch {
      // Cosmetic dismissal must not affect access or plan state.
    }
    setDismissed(true);
    emitProductAnalytics('upgrade_prompt_dismissed', {
      reason: 'free_status',
      surface: 'free_status_strip',
      usageState,
      billingAuthority: conversion.canManageBilling,
      returnAction: 'dashboard',
    });
  };

  return (
    <aside
      className="border-b border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-emerald-50 px-4 py-2.5 text-slate-900"
      aria-label="Free workspace status"
      data-testid="free-status-strip"
    >
      <div className="mx-auto flex max-w-[1200px] flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 text-sm">
          <strong className="font-semibold">{primary}</strong>
          <span className="ml-0 block text-slate-600 sm:ml-2 sm:inline">{supporting}</span>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => conversion.openPlans({reason: 'free_status', returnAction: 'dashboard'})}
            className="inline-flex min-h-9 items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold text-indigo-700 transition hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            aria-label="See Company plan for this Free workspace"
          >
            See Company plan
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition hover:bg-white hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            aria-label="Dismiss Free workspace status"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </aside>
  );
}
